import { IServiceVideoChatDevice } from "@obsbot/remo-services";
import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";
import PreviewCamSVG from "./icons/PreviewCam.svg";
import { SettingSelector, SettingSwitch, useSafePromise } from "remo-components";
import { SettingComponentsType } from "..";
import { eventBus, I420Canvas3DRenderer } from "@obsbot/remo-utils";
import { useChannelStore } from "@obsbot/remo-stores";

const VideoSettingPage = ({ t, rtc, preference }: SettingComponentsType): ReactElement => {
    // 相机设备列表
    const [camDeviceList, setCamDeviceList] = useState<IServiceVideoChatDevice[]>([]);
    // 当前的相机设备ID
    const [currentDeviceID, setCurrentDeviceID] = useState<string | null>(null);
    // 预览视频的显示区域
    const previewEl = useRef<HTMLDivElement>(null);
    // 是否正在预览视频
    const [isPreviewing, setIsPreviewing] = useState(false);
    // 是否总是预览视频
    const [isAlwaysPreview, setIsAlwaysPreview] = useState(preference?.alwaysPreviewCam ?? false);
    // 是否启用画面降噪
    const [isEnableDenoise, setIsEnableDenoise] = useState(
        preference?.enabledVideoDenoise ?? false,
    );
    // 是否启用硬件加速
    const [isEnableHWAcceleration, setIsEnableHWAcceleration] = useState(
        preference?.enabledHardwareAcceleration ?? false,
    );
    // 是否启用视频镜像
    const [isEnableMirror, setIsEnableMirror] = useState(preference?.videoMirror ?? false);
    // 是否总是视频开黑
    const [isAlwaysVideo, setIsAlwaysVideo] = useState(preference?.alwaysVideo ?? false);
    const sp = useSafePromise();
    const channelStore = useChannelStore();
    // 获取相机设备列表
    useEffect(() => {
        const updateCameraDevices = async (deviceID?: string): Promise<void> => {
            const devices = await sp(rtc.getCameraDevices());
            setCamDeviceList(devices);
            if (devices.length === 0) {
                setCurrentDeviceID(null);
                return;
            }
            const camId = deviceID || rtc.getCameraID() || devices[0]?.deviceId;
            setCurrentDeviceID(camId || null);
            if (devices.every(device => device.deviceId !== camId)) {
                setCurrentDeviceID(null);
            }
            if (camId) {
                onCameraChanged(camId);
            }
        };
        updateCameraDevices();
        rtc.events.on("camera-changed", updateCameraDevices);
        return () => {
            rtc.events.off("camera-changed", updateCameraDevices);
        };
    }, [rtc, sp]);
    // 处理选择相机
    const onCameraChanged = useCallback(
        (deviceID: string): void => {
            rtc.setCameraID(deviceID);
        },
        [rtc],
    );
    useEffect(() => {
        if (!rtc) {
            return;
        }
        const canvasContainer = previewEl.current as HTMLDivElement;
        const canvas = canvasContainer?.children[0]?.children[0] as HTMLCanvasElement;
        let renderer: I420Canvas3DRenderer;
        if (canvas && canvasContainer && canvas.tagName === "CANVAS") {
            renderer = new I420Canvas3DRenderer(canvas);
        }
        const handlePreviewVideoData = ({
            data,
            width,
            height,
        }: {
            data: Uint8Array;
            width: number;
            height: number;
        }): void => {
            if (isPreviewing) {
                if (canvas && canvasContainer && canvas.tagName === "CANVAS") {
                    renderer.draw(data, width, height, isEnableMirror ? 360 : 0);
                }
            }
        };
        rtc.events.on("preview-video-stream-data", handlePreviewVideoData);
        return () => {
            rtc.events.off("preview-video-stream-data", handlePreviewVideoData);
        };
    }, [rtc, isPreviewing, isEnableMirror]);
    // 处理预览相机
    const clickPreviewCamera = useCallback((): void => {
        if (channelStore.userState.camera) {
            eventBus.emit("show-message", {
                message: t("settings.rtc-setting.cameraInUse"),
                type: "warning",
            });
            return;
        }
        if (currentDeviceID && previewEl.current) {
            rtc.startCameraTest(previewEl.current);
            setIsPreviewing(true);
        }
    }, [rtc, currentDeviceID, channelStore.userState.camera, t]);
    useEffect(
        () => () => {
            return rtc.stopCameraTest();
        },
        [rtc],
    );
    const handleAlwaysPreviewChange = (checked: boolean): void => {
        setIsAlwaysPreview(checked);
        preference?.updateAlwaysPreviewCam(checked);
    };
    const handleEnableDenoiseChange = (checked: boolean): void => {
        setIsEnableDenoise(checked);
        preference?.updateEnabledVideoDenoise(checked);
    };
    const handleEnabledHWAccelerationChange = (checked: boolean): void => {
        setIsEnableHWAcceleration(checked);
        preference?.updateEnabledHardwareAcceleration(checked);
    };
    const handleVideoMirrorChange = (checked: boolean): void => {
        setIsEnableMirror(checked);
        preference?.updateVideoMirror(checked);
    };
    useEffect(() => {
        rtc.setVideoMirror(isEnableMirror);
    }, [rtc, isEnableMirror]);
    // 处理是否总是视频开黑
    const handleAlwaysVideoChange = (checked: boolean): void => {
        setIsAlwaysVideo(checked);
        preference?.updateAlwaysVideo(checked);
    };
    return (
        <div className="rtc-setting-page rtc-setting-video">
            <div className="rtc-setting-page-preview-rtc">
                <div className="rtc-setting-page-title">
                    {t("settings.rtc-setting.videoPreview")}
                </div>
                <div
                    ref={previewEl}
                    className={"rtc-setting-video-preview" + (isEnableMirror ? " mirror" : "")}
                >
                    <div className="iconAText">
                        <div className="icon">
                            <img src={PreviewCamSVG} />
                        </div>
                        <div className="text" onClick={clickPreviewCamera}>
                            {t("settings.rtc-setting.testVideo")}
                        </div>
                    </div>
                </div>
            </div>
            <div className="rtc-setting-page-video-advance">
                <div className="rtc-setting-page-content" style={{ marginBottom: 20 }}>
                    <div className="rtc-setting-show-item with-tips" style={{ marginBottom: 30 }}>
                        <div className="rtc-setting-page-label rtc-setting-page-with-tips">
                            <div className="text">{t("settings.rtc-setting.alwaysVideo")}</div>
                            <div className="tips">{t("settings.rtc-setting.alwaysVideoTip")}</div>
                        </div>
                        <div className="rtc-setting-page-input">
                            <SettingSwitch
                                checked={isAlwaysVideo}
                                onChange={handleAlwaysVideoChange}
                            />
                        </div>
                    </div>
                    <div className="rtc-setting-show-item with-tips">
                        <div className="rtc-setting-page-label rtc-setting-page-with-tips">
                            <div className="text">{t("settings.rtc-setting.alwaysPreview")}</div>
                            <div className="tips">{t("settings.rtc-setting.alwaysPreviewTip")}</div>
                        </div>
                        <div className="rtc-setting-page-input">
                            <SettingSwitch
                                checked={isAlwaysPreview}
                                onChange={handleAlwaysPreviewChange}
                            />
                        </div>
                    </div>
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">{t("previewCam.camDevice")}</div>
                        <div className="rtc-setting-page-input wide">
                            <SettingSelector
                                key={currentDeviceID}
                                className="rtc-setting-selector"
                                defaultValue={currentDeviceID}
                                disabled={camDeviceList.length === 0}
                                options={camDeviceList.map(device => ({
                                    label: device.label,
                                    value: device.deviceId,
                                    title: "",
                                }))}
                                placement="topLeft"
                                onChange={onCameraChanged}
                            />
                        </div>
                    </div>
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">
                            {t("settings.rtc-setting.camSuppression")}
                        </div>
                        <div className="rtc-setting-page-input">
                            <SettingSwitch
                                checked={isEnableDenoise}
                                onChange={handleEnableDenoiseChange}
                            />
                        </div>
                    </div>
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">
                            {t("settings.rtc-setting.hardwareAcceleration")}
                        </div>
                        <div className="rtc-setting-page-input">
                            <SettingSwitch
                                checked={isEnableHWAcceleration}
                                onChange={handleEnabledHWAccelerationChange}
                            />
                        </div>
                    </div>
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">
                            {t("settings.rtc-setting.videoMirror")}
                        </div>
                        <div className="rtc-setting-page-input">
                            <SettingSwitch
                                checked={isEnableMirror}
                                onChange={handleVideoMirrorChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoSettingPage;
